import { COLUMN_FORMATTING_TYPE } from '@/constants/common.constant'
import {
  AgGridCellValueMethod,
  AxiomReportType,
} from '@/enums/axiom-report.enum'
import {
  IScenarioAnalysisReportOverviewDTO,
  IScenarioSecurityDetailDTO,
  ITotalContributionChartData,
} from '@/interfaces/scenarioAnalysisReportComponents.interface'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import {
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core'
import _ from 'lodash'
import {
  customDateComparator,
  formatCellValue,
  getCellValue,
  gridDateFormatter,
} from '../tableGridUtils'
import { formatNumber, removeCommaFromString } from '../utils'

export const convertScenarioNameToScenarioKey = (scenarioName: string) =>
  scenarioName.toLowerCase().replace(/ /g, '_')

export const transformScenarioAnalysisData = (
  data: IScenarioAnalysisReportOverviewDTO[],
) => {
  let dataPoints: string[] = []
  if (data?.length) {
    const objForKey: any = _.cloneDeep(data[0])
    delete objForKey['analysis_date']
    delete objForKey['data_set']
    delete objForKey['scenario_name']
    delete objForKey['report_id']
    dataPoints = Object.keys(objForKey)
  }
  const result: any[] = []
  dataPoints?.forEach((key) => {
    const contributionMap = data.reduce((accumulator: any, current: any) => {
      const scenarioKey = convertScenarioNameToScenarioKey(
        current.scenario_name,
      ) // Transform scenario name to key format
      const contributionValue = current[key] // Round to 2 decimals and default to 0 if null/undefined

      if (!accumulator[scenarioKey]) {
        accumulator[scenarioKey] = {
          portfolio: 0,
          benchmark: 0,
          scenarioName: current.scenario_name,
        }
      }

      if (current.data_set === 'P') {
        accumulator[scenarioKey].portfolio = contributionValue
      } else if (current.data_set === 'B') {
        accumulator[scenarioKey].benchmark = contributionValue
      }

      return accumulator
    }, {})
    const aggregatedContribution = {
      returnType: key,
      returnTypeName:
        (getColumnDetail(key, AxiomReportType.SCEN) &&
          getColumnDetail(key, AxiomReportType.SCEN).header) ||
        _.words(key)
          ?.map((n: string) => _.capitalize(n))
          .join(' '),
      ...contributionMap,
    }
    result.push(aggregatedContribution)
  })
  return result
}

export const transformForChartData = (returnType: string, data: any[]) => {
  const contributionData = data.find((item) => item.returnType === returnType)

  if (!contributionData) {
    // console.warn(`No data found for return contribution type: ${returnType}`)
    return
  }

  const categories = []
  const portfolioData = []
  const benchmarkData = []

  for (const key in contributionData) {
    if (contributionData.hasOwnProperty(key)) {
      const returnContribution = contributionData[key]
      if (
        typeof returnContribution === 'object' &&
        returnContribution !== null &&
        'scenarioName' in returnContribution &&
        'portfolio' in returnContribution &&
        'benchmark' in returnContribution
      ) {
        categories.push(returnContribution.scenarioName)
        portfolioData.push(
          Number(
            removeCommaFromString(
              String(formatNumber(returnContribution.portfolio)),
            ),
          ),
        )
        benchmarkData.push(
          Number(
            removeCommaFromString(
              String(formatNumber(returnContribution.benchmark)),
            ),
          ),
        )
      }
    }
  }
  const result = {
    categories: categories,
    portfolio: portfolioData,
    benchmark: benchmarkData,
    returnType: returnType,
  }
  return result
}

export const generateRows = (data: IScenarioSecurityDetailDTO[]) => {
  return data.map((item) => {
    const commonData = {
      security_id: item.common.security_id,
      description: item.common.description,
      notional_weight: item.common.notional_weight,
      holdings: item.common.holdings,
      coupon: item.common.coupon,
      maturity_date: item.common.maturity_date,
      sectornm: item.common.sectornm,
      country_issue: item.common.country_issue,
      currency: item.common.currency,
      moody: item.common.moody,
      yte: item.common.yte,
      edur: item.common.edur,
    }
    const row: any = {
      ...commonData,
    }
    item.scenarios.forEach((scenario: any) => {
      row[`security_ret_${scenario.scenario_name}`] = scenario.security_ret
      row[`yield_ret_${scenario.scenario_name}`] = scenario.yield_ret
      row[`currency_ret_${scenario.scenario_name}`] = scenario.currency_ret
      row[`security_ret_contrib_${scenario.scenario_name}`] =
        scenario.security_ret_contrib
      row[`d1_ret_${scenario.scenario_name}`] = scenario.d1_ret
      row[`d2_ret_${scenario.scenario_name}`] = scenario.d2_ret
      row[`d3_ret_${scenario.scenario_name}`] = scenario.d3_ret
      row[`convex_ret_${scenario.scenario_name}`] = scenario.convex_ret
      row[`subset_ret_${scenario.scenario_name}`] = scenario.subset_ret
      row[`value_ret_${scenario.scenario_name}`] = scenario.value_ret
    })
    return row
  })
}

export const generateColumns = (data: IScenarioSecurityDetailDTO[]) => {
  const commonColumns = {
    headerName: 'Security Details',
    marryChildren: true,
    children: [
      {
        field: 'security_id',
        headerName:
          getColumnDetail('security_id', AxiomReportType.SCEN).header ||
          'Identifier',
        suppressMovable: true,
        pinned: 'left',
        hide: false,
        tooltipField: 'security_id',
        headerTooltip:
          getColumnDetail('security_id', AxiomReportType.SCEN)?.header ||
          'Identifier',
      },
      {
        field: 'description',
        headerName:
          getColumnDetail('description', AxiomReportType.SCEN)?.header ||
          'Description',
        suppressMovable: true,
        pinned: 'left',
        hide: false,
        tooltipField: 'description',
        headerTooltip:
          getColumnDetail('description', AxiomReportType.SCEN)?.header ||
          'Description',
      },
      {
        field: 'notional_weight',
        headerName:
          getColumnDetail('notional_weight', AxiomReportType.SCEN)?.header ||
          'Weight',
        suppressMovable: true,
        pinned: 'left',
        hide: false,
        tooltipField: 'notional_weight',
        headerTooltip:
          getColumnDetail('notional_weight', AxiomReportType.SCEN)?.header ||
          'Weight',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'notional_weight',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            'notional_weight',
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'notional_weight',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            'notional_weight',
          ),
      },

      {
        field: 'holdings',
        headerName:
          getColumnDetail('holdings', AxiomReportType.SCEN)?.header ||
          'Holdings',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        tooltipField: 'holdings',
        filter: 'agNumberColumnFilter',
        headerTooltip:
          getColumnDetail('holdings', AxiomReportType.SCEN)?.header ||
          'Holdings',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'holdings',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            'holdings',
            0,
            0,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'holdings',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            'holdings',
            0,
            0,
          ),
      },
      {
        field: 'sectornm',
        headerName:
          getColumnDetail('sectornm', AxiomReportType.SCEN)?.header || 'Sector',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        tooltipField: 'sectornm',
        headerTooltip:
          getColumnDetail('sectornm', AxiomReportType.SCEN)?.header || 'Sector',
      },
      {
        field: 'coupon',
        headerName:
          getColumnDetail('coupon', AxiomReportType.SCEN)?.header ||
          'Coupon Rate',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'coupon',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            'coupon',
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'coupon',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            'coupon',
          ),
        tooltipField: 'coupon',
        headerTooltip:
          getColumnDetail('coupon', AxiomReportType.SCEN)?.header ||
          'Coupon Rate',
      },
      {
        field: 'maturity_date',
        headerName:
          getColumnDetail('maturity_date', AxiomReportType.SCEN)?.header ||
          'Maturity Date',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        filter: 'agDateColumnFilter',
        valueFormatter: gridDateFormatter,
        filterParams: {
          comparator: customDateComparator,
          buttons: ['reset'],
        },
        tooltipValueGetter: gridDateFormatter,
        headerTooltip:
          getColumnDetail('maturity_date', AxiomReportType.SCEN)?.header ||
          'Maturity Date',
      },
      {
        field: 'country_issue',
        headerName:
          getColumnDetail('country_issue', AxiomReportType.SCEN)?.header ||
          'Country of Issue',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        tooltipField: 'country_issue',
        headerTooltip:
          getColumnDetail('country_issue', AxiomReportType.SCEN)?.header ||
          'Country of Issue',
      },
      {
        field: 'currency',
        headerName:
          getColumnDetail('currency', AxiomReportType.SCEN)?.header ||
          'Currency',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        tooltipField: 'currency',
        headerTooltip:
          getColumnDetail('currency', AxiomReportType.SCEN)?.header ||
          'Currency',
      },
      {
        field: 'moody',
        headerName:
          getColumnDetail('moody', AxiomReportType.SCEN)?.header ||
          "Moody's Quality",
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        tooltipField: 'moody',
        headerTooltip:
          getColumnDetail('moody', AxiomReportType.SCEN)?.header ||
          "Moody's Quality",
      },
      {
        field: 'yte',
        headerName:
          getColumnDetail('yte', AxiomReportType.SCEN)?.header ||
          'Yield to Effective Maturity',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'yte',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            'yte',
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'yte',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            'yte',
          ),
        tooltipField: 'yte',
        headerTooltip:
          getColumnDetail('yte', AxiomReportType.SCEN)?.header ||
          'Yield to Effective Maturity',
      },
      {
        field: 'edur',
        headerName:
          getColumnDetail('edur', AxiomReportType.SCEN)?.header ||
          'Effective Duration',
        suppressMovable: true,
        pinned: 'left',
        hide: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'edur',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            'edur',
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'edur',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            'edur',
          ),
        tooltipField: 'edur',
        headerTooltip:
          getColumnDetail('edur', AxiomReportType.SCEN)?.header ||
          'Effective Duration',
      },
    ],
  }
  const scenarioColumns = data[0].scenarios.map((scenario: any) => ({
    headerName: scenario.scenario_name,
    marryChildren: true,
    children: [
      {
        field: `security_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('security_ret', AxiomReportType.SCEN)?.header ||
          'Security Return',
        hide: true,
        tooltipField: `security_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('security_ret', AxiomReportType.SCEN)?.header ||
          'Security Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            `security_ret`,
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `security_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            `security_ret`,
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `security_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `security_ret_contrib_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('security_ret_contrib', AxiomReportType.SCEN)
            ?.header || 'Security Return Contribution',
        hide: false,
        tooltipField: `security_ret_contrib_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('security_ret_contrib', AxiomReportType.SCEN)
            ?.header || 'Security Return Contribution',
        filter: 'agNumberColumnFilter',

        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'security_ret_contrib',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `security_ret_contrib_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'security_ret_contrib',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `security_ret_contrib_${scenario.scenario_name}`,
          ),
      },
      {
        field: `yield_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('yield_ret', AxiomReportType.SCEN)?.header ||
          'Yield Return',
        hide: true,
        tooltipField: `yield_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('yield_ret', AxiomReportType.SCEN)?.header ||
          'Yield Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'yield_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `yield_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'yield_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `yield_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `currency_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('currency_ret', AxiomReportType.SCEN)?.header ||
          'Currency Return',
        hide: true,
        tooltipField: `currency_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('currency_ret', AxiomReportType.SCEN)?.header ||
          'Currency Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'currency_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `currency_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'currency_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `currency_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `d1_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('d1_ret', AxiomReportType.SCEN)?.header ||
          '(D1) Level Return',
        hide: true,
        tooltipField: `d1_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('d1_ret', AxiomReportType.SCEN)?.header ||
          '(D1) Level Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'd1_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `d1_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'd1_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `d1_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `d2_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('d2_ret', AxiomReportType.SCEN)?.header ||
          '(D2) Slope Return',
        hide: true,
        tooltipField: `d2_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('d2_ret', AxiomReportType.SCEN)?.header ||
          '(D2) Slope Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'd2_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `d2_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'd2_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `d2_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `d3_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('d3_ret', AxiomReportType.SCEN)?.header ||
          '(D3) Curvature Return',
        hide: true,
        tooltipField: `d3_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('d3_ret', AxiomReportType.SCEN)?.header ||
          '(D3) Curvature Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'd3_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `d3_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'd3_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `d3_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `convex_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('convex_ret', AxiomReportType.SCEN)?.header ||
          'Convexity Return',
        hide: true,
        tooltipField: `convex_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('convex_ret', AxiomReportType.SCEN)?.header ||
          'Convexity Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'convex_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `convex_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'convex_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `convex_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `subset_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('subset_ret', AxiomReportType.SCEN)?.header ||
          'Subset Return',
        hide: true,
        tooltipField: `subset_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('subset_ret', AxiomReportType.SCEN)?.header ||
          'Subset Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'subset_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `subset_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'subset_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `subset_ret_${scenario.scenario_name}`,
          ),
      },
      {
        field: `value_ret_${scenario.scenario_name}`,
        headerName:
          getColumnDetail('value_ret', AxiomReportType.SCEN)?.header ||
          'Valuation Return',
        hide: true,
        tooltipField: `value_ret_${scenario.scenario_name}`,
        headerTooltip:
          getColumnDetail('value_ret', AxiomReportType.SCEN)?.header ||
          'Valuation Return',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          getUpdatedValue(
            params,
            'value_ret',
            AgGridCellValueMethod.VALUE_GETTER,
            AxiomReportType.SCEN,
            `value_ret_${scenario.scenario_name}`,
          ),
        valueFormatter: (params: ValueFormatterParams) =>
          getUpdatedValue(
            params,
            'value_ret',
            AgGridCellValueMethod.VALUE_FORMATTER,
            AxiomReportType.SCEN,
            `value_ret_${scenario.scenario_name}`,
          ),
      },
    ],
  }))

  return [commonColumns, ...scenarioColumns]
}

export const columnDefs: any[] = [
  {
    headerName: 'Return Contribution (%)',
    field: 'returnTypeName',
    sortable: true,
    filter: true,
  },
]
export const getAllScenarios = (data: IScenarioAnalysisReportOverviewDTO[]) =>
  Array.from(
    new Set(
      data?.map(
        (item: IScenarioAnalysisReportOverviewDTO) => item?.scenario_name,
      ),
    ),
  )
export const getColDefsForScenarioOverview = (
  scenarios: string[],
  showBenchmarkData: boolean,
) => {
  const colDef: any[] = [
    {
      headerName: 'Values',
      field: 'returnTypeName',
      sortable: true,
      filter: 'agTextColumnFilter',
      width: 320,
      suppressMovable: true,
      lockPosition: true,
      pinned: 'left',
    },
  ]

  const scenarioMappings = scenarios?.map((scenario: string) => ({
    scenarioKey: convertScenarioNameToScenarioKey(scenario),
    scenarioName: scenario,
  }))

  scenarioMappings?.forEach(
    (item: { scenarioKey: string; scenarioName: string }) => {
      const { scenarioKey, scenarioName } = item
      colDef.push({
        headerName: scenarioName,
        children: [
          {
            headerName: 'Portfolio',
            field: `${scenarioKey}.portfolio`,
            headerTooltip: 'Portfolio',
            tooltipField: `${scenarioKey}.portfolio`,
            sortable: true,
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
            valueGetter: (params: ValueGetterParams) => {
              const field = `${scenarioKey}.portfolio`
              return getUpdatedValue(
                { data: { [field]: params.data?.[scenarioKey]?.portfolio } },
                params?.data?.returnType,
                AgGridCellValueMethod.VALUE_GETTER,
                AxiomReportType.SCEN,
                field,
              )
            },
            valueFormatter: (params: ValueFormatterParams) => {
              const field = `${scenarioKey}.portfolio`
              return getUpdatedValue(
                { data: { [field]: params.data?.[scenarioKey]?.portfolio } },
                params?.data?.returnType,
                AgGridCellValueMethod.VALUE_FORMATTER,
                AxiomReportType.SCEN,
                field,
              )
            },
          },
          {
            headerName: 'Benchmark',
            field: `${scenarioKey}.benchmark`,
            headerTooltip: 'Benchmark',
            tooltipField: `${scenarioKey}.benchmark`,
            sortable: true,
            hide: !showBenchmarkData,
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
            valueGetter: (params: ValueGetterParams) => {
              const field = `${scenarioKey}.benchmark`
              return getUpdatedValue(
                { data: { [field]: params.data?.[scenarioKey]?.benchmark } },
                params?.data?.returnType,
                AgGridCellValueMethod.VALUE_GETTER,
                AxiomReportType.SCEN,
                field,
              )
            },
            valueFormatter: (params: ValueFormatterParams) => {
              const field = `${scenarioKey}.benchmark`
              return getUpdatedValue(
                { data: { [field]: params.data?.[scenarioKey]?.benchmark } },
                params?.data?.returnType,
                AgGridCellValueMethod.VALUE_FORMATTER,
                AxiomReportType.SCEN,
                field,
              )
            },
          },
        ],
      })
    },
  )
  return colDef
}

export const transformToScenarioByWeightChartData = (
  data: IScenarioSecurityDetailDTO[],
) => {
  const transformedData: any[] = []

  const securitiesByWeight = _.orderBy(
    data,
    [(item) => item.common.notional_weight],
    ['desc'],
  )

  const top10SecuritiesByWeight = securitiesByWeight.slice(0, 10)
  top10SecuritiesByWeight.forEach((item) => {
    const row: any = {
      name: 'Notional Weight',
      y: item.common.notional_weight,
      labelToDisplay: item.common.security_id,
      otherDetails: item.common,
      //percentContribution: item.common.notional_weight.toFixed(2),
    }
    transformedData.push(row)
  })

  if (data?.length > 10) {
    const otherSecuritiesByWeight = securitiesByWeight.slice(10)
    let sumOfOtherSecuritiesByWeightNotionalWeight = 0
    otherSecuritiesByWeight?.forEach((secutity) => {
      sumOfOtherSecuritiesByWeightNotionalWeight +=
        secutity.common.notional_weight
    })
    transformedData.push({
      name: 'Notional Weight',
      y: sumOfOtherSecuritiesByWeightNotionalWeight,
      labelToDisplay: 'Others',
      otherDetails: 'N/A',
      //percentContribution: item.common.notional_weight.toFixed(2),
    })
  }

  return transformedData
}

export const transformToTotalContributionChartData = (
  data: IScenarioSecurityDetailDTO[],
) => {
  const chartData: ITotalContributionChartData = {
    data: [],
    series: [],
  }

  data.forEach((item: IScenarioSecurityDetailDTO) => {
    item?.scenarios?.forEach((sc: any) => {
      const currIndexInChartData = chartData.data?.findIndex(
        (x: any) => x.name === sc.scenario_name,
      )
      if (currIndexInChartData === -1) {
        chartData.data.push({
          name: sc.scenario_name,
          y: sc.security_ret_contrib,
          drilldown: sc.scenario_name,
        })
      } else {
        chartData.data[currIndexInChartData].y += sc.security_ret_contrib
      }

      const currIndexInChartSeries = chartData.series?.findIndex(
        (x: any) => x.name === sc.scenario_name,
      )

      if (currIndexInChartSeries === -1) {
        chartData.series.push({
          name: sc.scenario_name,
          id: sc.scenario_name,
          data: [[item.common.security_id, sc.security_ret_contrib]],
        })
      } else {
        chartData.series[currIndexInChartSeries].data.push([
          item.common.security_id,
          sc.security_ret_contrib,
        ])
      }
    })
  })
  return chartData
}

export const getUpdatedValue = (
  params: any,
  field: string,
  typeGetterOrSetter: string,
  reportType: string,
  transformField: string,
  minFractionDigits?: number,
  maxFractionDigits?: number,
) => {
  const columnMetaData = getColumnDetail(field, reportType)
  const value = getCellValue(
    params.data,
    transformField,
    typeGetterOrSetter,
    minFractionDigits,
    maxFractionDigits,
  )
  if (
    typeGetterOrSetter === AgGridCellValueMethod.VALUE_FORMATTER &&
    columnMetaData &&
    Object.keys(columnMetaData).length
  ) {
    const unit = COLUMN_FORMATTING_TYPE[columnMetaData.format.toLowerCase()]
    if (unit) {
      return formatCellValue(value, columnMetaData.format)
    } else {
      return value
    }
  }
  return value
}

export const getTop5AndBottom5Securities = (
  data: { name: string; id: string; data: [string, number][] }[],
) => {
  return data.map((item) => {
    const sortedData = item.data.sort((a, b) => b[1] - a[1])
    const top5Data = sortedData.slice(0, 5)
    let bottom5Data: [string, number][] = []
    if (sortedData.length > 5) {
      bottom5Data = sortedData.slice(-5)
    }

    return {
      ...item,
      data: [...top5Data, ...bottom5Data],
    }
  })
}
